import { Trans, useTranslation } from 'next-i18next';

import { V1A_IMG, V1A_NAMESPACE } from '../constants';

const PRO_SIZES = '(min-width: 90em) 470px, (min-width: 64em) 31vw, (min-width: 48em) 48vw, 100vw';
const PRO_WIDTHS = [240, 480, 800, 1200, 1536] as const;

const PROS = [
  { id: 'sacy', name: 'Sacy', game: 'Valorant', top: 'v1-pro__top--cut', image: 'v1-pro__cut' },
  { id: 'coldzera', name: 'coldzera', game: 'Counter-Strike 2', top: 'v1-pro__top--photo', image: 'v1-pro__img' },
  { id: 'brtt', name: 'brTT', game: 'League of Legends', top: 'v1-pro__top--cut v1-pro__top--brtt', image: 'v1-pro__cut' },
] as const;

function srcSet(id: string, format: 'avif' | 'webp') {
  return PRO_WIDTHS.map((width) => `${V1A_IMG}/pro-${id}-${width}.${format} ${width}w`).join(', ');
}

function Face({ id, size, alt }: { id: string; size: string; alt: string }) {
  return (
    <picture>
      <source type='image/avif' srcSet={`${V1A_IMG}/face-${id}-128.avif 128w, ${V1A_IMG}/face-${id}-256.avif 256w`} sizes={size} />
      <img className='v1-face block object-cover' src={`${V1A_IMG}/face-${id}-128.webp`} srcSet={`${V1A_IMG}/face-${id}-128.webp 128w, ${V1A_IMG}/face-${id}-256.webp 256w`} sizes={size} width='256' height='256' alt={alt} loading='lazy' decoding='async' />
    </picture>
  );
}

export function Pros() {
  const { t } = useTranslation(V1A_NAMESPACE);

  return (
    <section className='v1-pros relative isolate overflow-x-clip border-t border-[rgb(255_255_255/.08)] bg-[linear-gradient(180deg,#0E1116,#0B0D10)] py-[clamp(3rem,1.5rem_+_5vw,6rem)]' aria-labelledby='v1-pros-title'>
      <div className='v1-pros__bg' aria-hidden='true' />
      <div className='mx-auto w-full max-w-[90rem] px-[var(--gutter)]'>
        <div className='v1-head v1-head--center mx-auto max-w-[46rem] text-center [&_.v1-lead]:mx-auto'>
          <p className='v1-eyebrow v1-eyebrow--center m-0 inline-flex items-center justify-center gap-2 text-[length:var(--t-micro)] font-semibold uppercase leading-[1.3] tracking-[.16em] text-[#9AA3B5]' data-reveal>
            <span className='v1-eyebrow__dot size-1.5 flex-none rounded-full bg-[#93FF18] shadow-[0_0_10px_rgb(147_255_24/.8)]' aria-hidden='true' />
            {t('pros.eyebrow')}
          </p>
          <h2 className='v1-h2 mt-3 text-balance text-[length:var(--t-h2)] font-bold leading-[1.04] tracking-[-.032em] text-[#FFFFFF] min-[1920px]:text-[length:3.5rem]' id='v1-pros-title' data-reveal>
            <Trans t={t} i18nKey='pros.title' components={{ hl: <span className='v1-hl !text-[#93FF18] [text-shadow:0_0_22px_rgb(147_255_24/.3)] forced-colors:!text-[LinkText]' /> }} />
          </h2>
          <p className='v1-lead mt-4 max-w-[44rem] text-pretty text-[length:var(--t-lead)] leading-[1.55] text-[#CBD5E1]' data-reveal>
            {t('pros.lead')}
          </p>
        </div>

        <div className='v1-pros__grid mt-[clamp(2rem,1rem_+_3vw,3rem)] grid gap-4 *:min-w-0 md:grid-cols-2 lg:grid-cols-3 lg:gap-5' data-reveal-group>
          {PROS.map((pro) => (
            <figure key={pro.id} className='v1-pro relative m-0 flex min-w-0 flex-col overflow-hidden'>
              <div className={`v1-pro__top ${pro.top} relative aspect-[3/2] h-auto flex-none overflow-hidden`}>
                <picture>
                  <source type='image/avif' srcSet={srcSet(pro.id, 'avif')} sizes={PRO_SIZES} />
                  <img className={`${pro.image} absolute inset-0 size-full max-w-none object-contain object-center`} src={`${V1A_IMG}/pro-${pro.id}-800.webp`} srcSet={srcSet(pro.id, 'webp')} sizes={PRO_SIZES} width='1536' height='1024' alt={t(`pros.items.${pro.id}.alt`)} loading='lazy' decoding='async' />
                </picture>
                <span className='v1-pro__tag absolute bottom-3.5 start-4 z-[1] inline-flex max-w-[calc(100%_-_2rem)] items-center gap-2 truncate px-[.6875rem] py-1.5 text-[length:var(--t-micro)] font-bold uppercase leading-[1.1] tracking-[.14em] text-[#FFFFFF]'>
                  <i className='v1-pro__game-dot size-[6px] flex-none' aria-hidden='true' />
                  {pro.game}
                </span>
              </div>
              <div className='v1-pro__body relative flex min-w-0 flex-1 flex-col px-5 pb-5 pt-[1.125rem]'>
                <span className='v1-pro__mark' aria-hidden='true'>
                  ”
                </span>
                <blockquote className='v1-pro__quote m-0 flex-1 hyphens-auto text-pretty text-[length:var(--t-body)] font-medium leading-[1.55] text-[#F4F6F7]'>{t(`pros.items.${pro.id}.quote`)}</blockquote>
                <figcaption className='v1-pro__cap mt-[1.125rem] flex items-center gap-2.5 text-[length:var(--t-micro)] [&_img]:size-10 [&_img]:flex-none [&_img]:object-cover'>
                  <Face id={pro.id} size='40px' alt='' />
                  <span className='v1-pro__cap-text block min-w-0 hyphens-auto [&_span]:block [&_span]:text-[#8C95A8] [&_strong]:block [&_strong]:text-[length:var(--fs-md)] [&_strong]:font-bold [&_strong]:leading-[1.2] [&_strong]:tracking-[-.01em] [&_strong]:text-[#FFFFFF]'>
                    <strong>{pro.name}</strong>
                    <span>{t(`pros.items.${pro.id}.role`)}</span>
                  </span>
                </figcaption>
              </div>
            </figure>
          ))}
        </div>

        <div className='v1-rating mt-[clamp(1.5rem,1rem_+_1.5vw,2rem)] flex flex-wrap items-center justify-center gap-x-4 gap-y-2.5 px-5 py-3.5 text-center' data-reveal>
          {/* role=list keeps list semantics that list-style:none removes in Safari/VoiceOver */}
          {/* eslint-disable-next-line jsx-a11y/no-redundant-roles */}
          <ul className='v1-rating__faces m-0 flex list-none !p-0 [&_img]:block [&_img]:size-9 [&_img]:object-cover [&_li:first-child]:ms-0 [&_li]:-ms-1.5' role='list' aria-label={t('pros.rating.faces_label')}>
            <li>
              <Face id='coldzera' size='36px' alt='coldzera' />
            </li>
            <li>
              <Face id='sacy' size='36px' alt='Sacy' />
            </li>
            <li>
              <span className='v1-rating__more tabular grid size-9 place-items-center text-[length:.6875rem] font-bold leading-none tracking-[.01em] !text-[#93FF18]' aria-hidden='true'>
                {t('pros.rating.more')}
              </span>
            </li>
          </ul>
          <p className='v1-rating__stars m-0 text-[length:var(--t-body)] tracking-[.1em] !text-[#93FF18]' aria-hidden='true'>
            ★★★★★
          </p>
          <p className='v1-rating__text m-0 text-[length:max(.75rem,var(--t-micro))] text-[#9AA3B5] [&_strong]:text-[length:var(--t-body)] [&_strong]:font-bold [&_strong]:text-[#FFFFFF]'>
            <Trans t={t} i18nKey='pros.rating.text' components={{ strong: <strong /> }} />
          </p>
        </div>
      </div>
    </section>
  );
}
